import { useCallback, useEffect, useState } from 'react';
import { COUNTRIES } from '../lib/shippingCountries';
import { countryCurrency, paypalCurrency } from '../lib/countryCurrencies';
import { cartTotals, convertUsd, formatMoney } from '../lib/currencyMoney';

const COUNTRY_KEY = 'guns_country';

function initialCountry() {
  try {
    const saved = localStorage.getItem(COUNTRY_KEY);
    if (COUNTRIES.some(country => country.code === saved)) return saved;
  } catch { /* Restricted browser storage: selection still works in memory. */ }
  return 'US';
}

function usdQuote(country) {
  return { country, currency: 'USD', rate: 1, paymentCurrency: 'USD', paymentRate: 1, updatedAt: null };
}

export default function useCountryPricing() {
  const [selectedCountry, setCountry] = useState(initialCountry);
  const [quote, setQuote] = useState(() => usdQuote('US'));
  const [failure, setFailure] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const country = COUNTRIES.find(item => item.code === selectedCountry);
  const currency = countryCurrency(selectedCountry);
  const paymentCurrency = paypalCurrency(currency);
  const error = failure?.country === selectedCountry ? failure.message : '';
  const ready = quote?.country === selectedCountry && !error;

  const setSelectedCountry = useCallback(code => {
    if (!COUNTRIES.some(item => item.code === code)) return;
    setCountry(code);
    try { localStorage.setItem(COUNTRY_KEY, code); } catch { /* Storage is optional. */ }
  }, []);

  useEffect(() => {
    if (currency === 'USD') {
      setQuote(usdQuote(selectedCountry));
      setFailure(null);
      return undefined;
    }
    const controller = new AbortController();
    setFailure(null);
    fetch(`/api/currency-quote?country=${selectedCountry}`, { signal: controller.signal })
      .then(async response => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Currency prices are unavailable.');
        if (data.country !== selectedCountry || data.currency !== currency
          || data.paymentCurrency !== paymentCurrency
          || !Number.isFinite(data.rate) || data.rate <= 0
          || !Number.isFinite(data.paymentRate) || data.paymentRate <= 0) {
          throw new Error('The currency quote could not be verified.');
        }
        if (!controller.signal.aborted) setQuote(data);
      })
      .catch(problem => {
        if (!controller.signal.aborted) setFailure({
          country: selectedCountry,
          message: problem.message || 'Currency prices are unavailable. Please retry.',
        });
      });
    return () => controller.abort();
  }, [selectedCountry, currency, paymentCurrency, attempt]);

  return {
    selectedCountry, setSelectedCountry, country, currency, paymentCurrency,
    ready, isLoading: !ready && !error, error, retry: () => setAttempt(value => value + 1),
    rateUpdatedAt: ready ? quote.updatedAt : null,
    formatPrice: amount => ready ? formatMoney(convertUsd(amount, quote.rate, currency), currency)
      : error ? 'Price unavailable' : 'Loading price…',
    formatLocal: amount => formatMoney(amount, currency),
    formatPayment: amount => formatMoney(amount, paymentCurrency),
    totals: (cart, shippingUsd) => ready ? {
      local: cartTotals(cart, shippingUsd, quote.rate, currency),
      payment: cartTotals(cart, shippingUsd, quote.paymentRate, paymentCurrency),
    } : null,
  };
}
